import {
    type ReactNode,
    Fragment,
    Children,
    type FC
} from "react";
import {
    View
} from "react-native";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    EmptyPartContentProps,
    IEmptyContentProps
} from "../../type";
import {
    getEmptyContentLayout
} from "../../util";
import {
    withEmptyContext
} from "../../context";
import stylesheet, {
    getEmptyActionSkeleton
} from "../../stylesheet";
import Skeleton from "../../../skeleton";

const EmptyContentBase: FC<EmptyPartContentProps<IEmptyContentProps>> = ({
    customTheme: customThemeProp,
    children,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        touchTargets
    } = NCoreUIKitTheme.useContext(customTheme);

    const layout = getEmptyContentLayout(children);

    const currentActionSkeleton = getEmptyActionSkeleton({
        actionSizes: layout.actionSizes,
        measures: context.measures,
        controlHeights,
        shapeConfigs,
        touchTargets
    });

    const isStacked = layout.groups.some((group) => group.isStacked);

    const rowDynamicStyle = {
        gap: context.measures.actionGap
    };

    const renderRowItems = (nodes: ReactNode) => {
        return Children.toArray(nodes).map((node, index) => <View
            style={stylesheet.contentItem}
            key={index}
        >
            {node}
        </View>);
    };

    if(context.isLoading) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.content,
                rowDynamicStyle
            ]}
        >
            <Skeleton
                height={currentActionSkeleton.height}
                width={currentActionSkeleton.width}
                customTheme={customTheme}
                radius="actions"
            />
        </View>;
    }

    if(!isStacked && (!layout.links.length || !layout.actions.length)) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.content,
                rowDynamicStyle
            ]}
        >
            {renderRowItems(children)}
        </View>;
    }

    const renderGroups = () => {
        return layout.groups.map((group, index) => {
            if(group.isStacked) {
                return <Fragment
                    key={index}
                >
                    {group.nodes}
                </Fragment>;
            }

            return <View
                style={[
                    stylesheet.content,
                    rowDynamicStyle
                ]}
                key={index}
            >
                {renderRowItems(group.nodes)}
            </View>;
        });
    };

    const renderActions = () => {
        if(!isStacked) {
            return <View
                style={[
                    stylesheet.content,
                    rowDynamicStyle
                ]}
            >
                {renderRowItems(layout.actions)}
            </View>;
        }

        return <View
            style={[
                stylesheet.contentStack,
                rowDynamicStyle
            ]}
        >
            {renderGroups()}
        </View>;
    };

    if(!layout.links.length) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.contentStack,
                rowDynamicStyle
            ]}
        >
            {renderGroups()}
        </View>;
    }

    return <View
        {...props}
        style={[
            style,
            stylesheet.contentStack,
            {
                gap: context.measures.gap
            }
        ]}
    >
        {renderActions()}
        <View
            style={[
                stylesheet.content,
                rowDynamicStyle
            ]}
        >
            {renderRowItems(layout.links)}
        </View>
    </View>;
};
export default withEmptyContext("content", EmptyContentBase);
